import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import VersionViewerModal from '../../../../components/TermsAndPolicies/VersionViewerModal';
import { getUserTermsStatus } from '../../../../api/General/termsAndPolicies';
import type { UserTermsStatusView } from '../../../../models/general';

interface Props { userId: string; }

function FieldRow({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-border/40 last:border-b-0">
            <dt className="w-44 shrink-0 text-xs uppercase tracking-wide text-muted-foreground font-medium pt-0.5">
                {label}
            </dt>
            <dd className="text-sm text-foreground min-w-0 flex-1" data-testid={testId}>
                {children}
            </dd>
        </div>
    );
}

const TermsAcceptanceSection: React.FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<UserTermsStatusView | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [versionOpen, setVersionOpen] = useState(false);
    const [versionToView, setVersionToView] = useState<number | null>(null);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getUserTermsStatus(userId)
            .then(res => {
                if (cancelled) return;
                if (res.errors?.length || !res.payload) {
                    setError(res.errors?.[0]?.description ?? t('Failed to load terms acceptance'));
                    setData(null);
                } else {
                    setData(res.payload);
                }
            })
            .catch(() => { if (!cancelled) setError(t('Failed to load terms acceptance')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId, t]);

    const openVersion = (v: number) => {
        setVersionToView(v);
        setVersionOpen(true);
    };

    const renderStatus = (d: UserTermsStatusView) => {
        if (!d.hasAccepted) {
            return (
                <span
                    data-testid="terms-status-badge"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200"
                >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t('Not accepted')}
                </span>
            );
        }
        if (d.isStale) {
            return (
                <span
                    data-testid="terms-status-badge"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span data-testid="terms-stale-badge">{t('Stale')}</span>
                </span>
            );
        }
        return (
            <span
                data-testid="terms-status-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
            >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t('Accepted')}
            </span>
        );
    };

    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t('Terms & Policies acceptance')}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {t('Whether this employee has accepted the company\'s current Terms & Policies, and which version they last agreed to.')}
                        </p>
                    </div>
                </div>

                {loading && (
                    <div className="text-sm text-muted-foreground" data-testid="terms-loading">
                        {t('Loading…')}
                    </div>
                )}

                {error && (
                    <div className="p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200" data-testid="terms-error">
                        {error}
                    </div>
                )}

                {!loading && !error && data && (
                    <dl data-testid="terms-acceptance-details">
                        <FieldRow label={t('Status')} testId="terms-status-row">
                            {renderStatus(data)}
                        </FieldRow>
                        <FieldRow label={t('Accepted version')} testId="terms-accepted-version">
                            {data.acceptedVersion != null ? (
                                <button
                                    type="button"
                                    onClick={() => openVersion(data.acceptedVersion!)}
                                    className="text-primary hover:underline"
                                    data-testid="terms-accepted-version-link"
                                >
                                    v{data.acceptedVersion}
                                </button>
                            ) : (
                                <span className="text-muted-foreground/60">—</span>
                            )}
                        </FieldRow>
                        <FieldRow label={t('Accepted at')} testId="terms-accepted-at">
                            {data.acceptedAt
                                ? new Date(data.acceptedAt).toLocaleString()
                                : <span className="text-muted-foreground/60">—</span>}
                        </FieldRow>
                        <FieldRow label={t('Current version')} testId="terms-current-version-row">
                            {data.hasPublishedTerms && data.currentVersion != null ? (
                                <button
                                    type="button"
                                    onClick={() => openVersion(data.currentVersion!)}
                                    className="text-primary hover:underline"
                                    data-testid="terms-current-version-link"
                                >
                                    v{data.currentVersion}
                                </button>
                            ) : (
                                <span className="text-muted-foreground/60">{t('No version has been published yet.')}</span>
                            )}
                        </FieldRow>
                    </dl>
                )}
            </CardContent>

            <VersionViewerModal
                version={versionToView}
                open={versionOpen}
                onOpenChange={setVersionOpen}
            />
        </Card>
    );
};

export default TermsAcceptanceSection;
